import { FC } from "react";

interface Props {
	authUser: AuthUserInfo | null;
}

const formatDate = (value: string | null): string => {
	if (!value) return "Never";
	const date = new Date(value);
	return date.toLocaleString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
};

const StatusBadge: FC<{ label: string; active: boolean }> = ({
	label,
	active
}) => (
	<span
		className={[
			"inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border",
			active
				? "bg-green-50 text-green-700 border-green-200"
				: "bg-zinc-100 text-zinc-500 border-zinc-200"
		].join(" ")}
	>
		{label}
	</span>
);

const PersonAdminTab: FC<Props> = ({ authUser }) => {
	if (!authUser) {
		return (
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<p className="text-sm text-zinc-500">No linked admin account.</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{/* Account info */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Account
				</label>
				<div className="flex flex-col gap-4">
					<div>
						<p className="text-sm font-medium text-zinc-600 mb-1">Username</p>
						<p className="text-sm text-foreground">{authUser.username}</p>
					</div>
					<div>
						<p className="text-sm font-medium text-zinc-600 mb-2">Status</p>
						<div className="flex flex-wrap gap-2">
							<StatusBadge label="Active" active={authUser.isActive} />
							<StatusBadge label="Staff" active={authUser.isStaff} />
							<StatusBadge label="Superuser" active={authUser.isSuperuser} />
						</div>
					</div>
				</div>
			</div>

			{/* Permission groups */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Permission Groups
				</label>
				{authUser.groups.length === 0 ? (
					<p className="text-sm text-zinc-500">
						No permission groups assigned.
					</p>
				) : (
					<ul className="flex flex-col gap-1.5">
						{authUser.groups.map((group) => (
							<li key={group.id} className="text-sm text-foreground">
								{group.name}
							</li>
						))}
					</ul>
				)}
			</div>

			{/* Dates */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Activity
				</label>
				<div className="flex flex-col gap-4">
					<div>
						<p className="text-sm font-medium text-zinc-600 mb-1">Last login</p>
						<p className="text-sm text-foreground">
							{formatDate(authUser.lastLogin)}
						</p>
					</div>
					<div>
						<p className="text-sm font-medium text-zinc-600 mb-1">
							Date joined
						</p>
						<p className="text-sm text-foreground">
							{formatDate(authUser.dateJoined)}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PersonAdminTab;
